import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  InputSignal,
  output,
  Output,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import {
  InvestigationResultDto,
  AssignCase,
  PDropdownOptions,
  User,
} from '../../../interfaces';
import { CaseService } from '../../../services/case.service';
import { API_URL, Roles } from '../../../utils';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { NgClass } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { ListboxModule } from 'primeng/listbox';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TranslationService } from '../../../services/translation.service';
import { UserService } from '../../../services';
import { InvestigationResultModalComponent } from '../investigation-result-modal/investigation-result-modal.component';
import { ChangeDetectorRef } from '@angular/core';
import { InvestigatorDto } from '../../../interfaces/case/InvestigatorDto';
import { AssessorsDto } from '../../../interfaces/case/AssessorsDto';
import { PanelModule } from 'primeng/panel';

@Component({
  selector: 'app-actions-card',
  standalone: true,
  imports: [
    FormsModule,
    DialogModule,
    MultiSelectModule,
    ConfirmDialogModule,
    NgClass,
    ButtonModule,
    ListboxModule,
    PanelModule,
    TextareaModule,
    ToastModule,
    SelectModule,
    TranslatePipe,
    InvestigationResultModalComponent
  ],
  templateUrl: './actions-card.component.html',
  styleUrl: './actions-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
})
export class ActionsCardComponent {
  translateService = inject(TranslationService);
  private userService = inject(UserService);
  api = inject(API_URL);

  @Input({ required: true }) claimStatus: string = '';
  @Input({ required: true }) currentUser: any;
  @Input({ required: true }) caseData: any;
  users: InputSignal<User[]> = input.required();
  @Input({ required: true }) investigationStatuses: any;
  investigationResults = input.required<InvestigationResultDto[]>();
  allInvestigationResults = signal<InvestigationResultDto[]>([]);

  @Output() claimStatusChange = new EventEmitter<any>();
  @Output() caseDataChange = new EventEmitter();
  @Output() savedAmountChange = new EventEmitter<number>();
  @Output() investigationCreated = new EventEmitter<void>();

  private translate: TranslateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  investigationResultsChange = output<InvestigationResultDto[]>();
  showInvestigationResultModal = signal<boolean>(false);
  isCoassess = false;

  caseManagerOptions = computed(() => this.userService.caseManagerOptions());

  investigatorOptions = computed(() => {
    if (this.api.includes('coassess')) {
      return this.userService.investigatorOptions();
    }

    return this.investigators().map(i => ({
      label: i.name,
      value: i.id
    }));
  });

  assessorOptions = computed(() => {

    return this.assessors()
    .filter( i => {
      if(!i.name) return false;
      const match = i.name.match(/^(.*)\((.*)\)$/);
      if(!match) return true;
      const namePart = match[1].trim();
      const codePart = match[2].trim();
      return !(namePart === '' && codePart === '0');
    })
    .map(i => ({
      label: i.name,
      value: i.id
    }));
  });


  investigators = signal<InvestigatorDto[]>([]);
  assessors = signal<AssessorsDto[]>([]);

  statusOptions: WritableSignal<PDropdownOptions[]> = signal([
    {
      label: this.translate.instant('CASE_DETAIL.APPROVE'),
      value: 'true',
    },
    {
      label: this.translate.instant('CASE_DETAIL.REJECT'),
      value: 'false',
    },
  ]);

  selectedCaseManagerId: number = 0;
  selectedInvestigatorId: number = 0;
  selectedAssessorId: number = 0;
  selectedStatus: string = '';

  roles = Roles;
  private caseService = inject(CaseService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private subscriptions: Subscription[] = [];

  selectedComments: any = null;
  temporaryResults: any[] = [];
  currentTemporaryResultId: any;

  selectedInvestigationResult: InvestigationResultDto = {
    comment: '',
    investigationResultId: '',
    investigator: '',
    visibilityStatus: '',
    tags: [],
    files: []
  };


  selectedTags: any;
  newComment: string | null = null;
  isButtonDisabled: boolean = false;

  toBeUploadedFiles: File[] = [];

  get isCaseDetailReadOnly(): boolean {
    return this.currentUser?.role === this.roles.Adjuster || this.currentUser?.role === this.roles.Guest || this.currentUser?.role === this.roles.Secretary;
  }

  get canAssignInvestigator(): boolean {
    return this.currentUser?.role === this.roles.Administrator || this.currentUser?.role === this.roles.Secretary;
  }

  ngOnInit(): void {
    if (this.api.includes('coassess')) {
      this.isCoassess = true;
      this.userService.loadCaseManagerOptions();
    }
    this.updateStatusOptions();
    if (this.api.includes('coreact') || this.api.includes('localhost')) {
      this.loadAssessorsByClaimId(this.caseData.claimId);
      this.loadInvestigators();
    }
  }

  loadInvestigators() {
    this.caseService.getInvestigators().subscribe({
      next: result => this.investigators.set(result),
      error: err => console.error('Failed to load investigators', err)
    });
  }

  loadAssessorsByClaimId(claimId: string = this.caseData.claimId) {
    this.caseService.getAssessorsByClaimId(claimId).subscribe({
      next: result => this.assessors.set(result),
      error: err => console.error('Failed to load assessors', err)
    });
  }

  onAssign(userId: string) {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    let assign: AssignCase = {
      userId: userId,
      caseIds: [this.caseData.caseId],
    };

    let selectedEntity = this.users().find(
      (user: any) => user.userId === assign.userId
    );

    const assingInvSubscription = this.caseService
      .assignToCase(assign)
      .subscribe({
        next: () => {
          const msg = this.translate.instant('TOAST.ASSIGN_SUCCESS');
          this.getToastMessage(true, msg);
        },
        error: () => {
          const msg = this.translate.instant('TOAST.ASSIGN_ERROR');
          this.getToastMessage(false, msg);
        },
        complete: () => {
          if (
            selectedEntity &&
            selectedEntity.role == this.roles[this.roles.CaseManager]
          ) {
            this.caseData.caseManager = `${selectedEntity.firstName} ${selectedEntity.lastName}`;
            this.caseDataChange.emit(this.caseData);
          } else if (
            selectedEntity &&
            selectedEntity.role == this.roles[this.roles.Investigator]
          ) {
            this.caseData.investigator = `${selectedEntity.firstName} ${selectedEntity.lastName}`;
            this.caseData.isPending = false;
            this.caseDataChange.emit(this.caseData);
          } else {
            const invOption = this.investigatorOptions().find(o => o.value === assign.userId);
            if (invOption) {
              this.caseData.investigator = invOption.label;
              this.caseData.isPending = false;
              this.caseDataChange.emit(this.caseData);
            } else {
              const cmOption = this.caseManagerOptions().find(o => o.value === assign.userId);
              if (cmOption) {
                this.caseData.caseManager = cmOption.label;
                this.caseDataChange.emit(this.caseData);
              }
            }
          }
          this.cdr.markForCheck();
        },
      });

    this.subscriptions.push(assingInvSubscription);
  }

  onAssignInvestigator(investigatorId: string) {
    if (!this.canAssignInvestigator) {
      return;
    }

    const assign: AssignCase = {
      userId: investigatorId,
      caseIds: [this.caseData.caseId],
    };

    const sub = this.caseService.assignToCase(assign).subscribe({
      next: () => {
        const msg = this.translate.instant('TOAST.ASSIGN_SUCCESS');
        this.getToastMessage(true, msg);

        const option = this.investigatorOptions().find(o => o.value === investigatorId);
        if (option) {
          this.caseData.investigator = option.label;
          this.caseData.isPending = false;
          this.caseDataChange.emit(this.caseData);
        }
      },
      error: () => {
        const msg = this.translate.instant('TOAST.ASSIGN_ERROR');
        this.getToastMessage(false, msg);
      }
    });
    this.subscriptions.push(sub);
  }
  onAssignAssessor(assessorId: string) {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    const assign: AssignCase = {
      userId: assessorId,
      caseIds: [this.caseData.caseId],
    };

    const sub = this.caseService.assignToCase(assign).subscribe({
      next: () => {
        const msg = this.translate.instant('TOAST.ASSIGN_SUCCESS');
        this.getToastMessage(true, msg);

        const option = this.assessorOptions().find(o => o.value === assessorId);
        if (option) {
          this.caseData.assessor = option.label;
          this.caseData.isPending = false;
          this.caseDataChange.emit(this.caseData);
        }
      },
      error: () => {
        const msg = this.translate.instant('TOAST.ASSIGN_ERROR');
        this.getToastMessage(false, msg);
      }
    });
    this.subscriptions.push(sub);
  }
  clearAssignments() {
    if (this.isCaseDetailReadOnly && this.currentUser?.role !== this.roles.Secretary) {
      return;
    }

    const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

    const assign: AssignCase = {
      userId: EMPTY_GUID,
      caseIds: [this.caseData.caseId],
    };

    const sub = this.caseService.assignToCase(assign).subscribe({
      next: () => {

        this.caseData.investigator = null;
        this.caseData.assessor = null;
        this.caseData.isPending = false;
        this.caseDataChange.emit(this.caseData);

        this.selectedInvestigatorId = 0;
        this.selectedAssessorId = 0;
      },
      error: () => {
        const msg = this.translate.instant('TOAST.ASSIGN_ERROR');
        this.getToastMessage(false, msg);
      }
    });

    this.subscriptions.push(sub);
  }
  onChangeCaseStatus(caseStatus: string) {
    //
    let status: boolean = caseStatus == 'true';
    status;

    const changeStatusSubscription = this.caseService
      .updateApprovalStatus(this.caseData.caseId, status)
      .subscribe({
        next: () => {
          const msg = this.translate.instant('TOAST.SUCCESSFULLY_CHANGED');
          this.getToastMessage(true, msg);
          this.caseData.isApproved = status;
          this.claimStatus = this.caseData;
          this.caseDataChange.emit(this.claimStatus);
        },
        error: (error) => {
          const msg = this.translate.instant('TOAST.ERROR_CHANGING');
          this.getToastMessage(false, msg);
        },
        complete: () => { },
      });

    this.subscriptions.push(changeStatusSubscription);
  }
  createTemporaryInvestigationComment(event: any) {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    const payload = event && event.result ? event.result : event;
    const commentBody = new FormData();
    const tags = payload?.tags ?? [];

    if (tags.length > 0) {
      tags.map((item: any) =>
        commentBody.append('Tags', item.name ?? item)
      );
    }
    if (payload?.comment) {
      commentBody.append('Comment', payload.comment);
    }
    this.toBeUploadedFiles.forEach((file: File) => {
      commentBody.append('Files', file, file.name);
    });

    this.isButtonDisabled = true;
    const createCommentSubscription$ = this.caseService
      .createInvestigatorResult(this.caseData.caseId, commentBody)
      .subscribe({
        next: () => {
          //window.location.reload();
        },
        error: (error) => {
          console.error('Error submitting comment:', error);
          this.newComment = '';
          delete this.selectedTags;
          const msg = this.translate.instant('TOAST.SOMETHING_WENT_WRONG');
          this.getToastMessage(false, msg);
        },
        complete: () => {
          this.investigationCreated.emit();
          this.cdr.markForCheck();
          this.newComment = '';
          delete this.selectedTags;

        },
      });

    this.subscriptions.push(createCommentSubscription$);
    this.showInvestigationResultModal.set(false);
  }

  get isButtonDisabledInvestigation(): boolean {
    return this.investigationResults().length > 0 || !this.caseData.investigator;
  }

  handleFilesToUpload(files: File[]) {
    const file = files[0] || null;
    this.toBeUploadedFiles = file ? [file] : [];

    this.selectedInvestigationResult = {
      ...this.selectedInvestigationResult,
      files: file
        ? [{ fileName: file.name, blobUri: '', isError: false }]
        : []
    };
  }

  handleSavedAmount(amount: number) {
    this.savedAmountChange.emit(amount);
  }


  onRemoveFile(file: any) {
    this.toBeUploadedFiles = [];
    this.selectedInvestigationResult = {
      ...this.selectedInvestigationResult,
      files: []
    };
  }

  private updateStatusOptions(): void {
    this.translateService.getTranslations(['CASE_DETAIL.APPROVE', 'CASE_DETAIL.REJECT']).subscribe(translations => {
      this.statusOptions.set([
        { label: translations['CASE_DETAIL.APPROVE'], value: 'true' },
        { label: translations['CASE_DETAIL.REJECT'], value: 'false' }
      ]);
    });
  }

  confirm() {
    this.confirmationService.confirm({
      header: 'Confirmation',
      message: 'Please confirm to proceed moving forward.',
      icon: 'pi pi-exclamation-circle',
      rejectButtonProps: {
        label: 'Cancel',
        icon: 'pi pi-times',
        outlined: true,
        size: 'small',
      },
      acceptButtonProps: {
        label: 'Save',
        icon: 'pi pi-check',
        size: 'small',
      },
      accept: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Confirmed',
          detail: 'You have accepted',
          life: 3000,
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Rejected',
          detail: 'You have rejected',
          life: 3000,
        });
      },
    });
  }
  // updateUploadedFiles(files: any[]) {
  //   this.toBeUploadedFiles =files
  //   //
  //   // this.toBeUploadedFiles = [...files];
  // }

  // updateUploadedFiles(files: any[]) {
  //   this.toBeUploadedFiles = files.map((file) => ({
  //     blobUri: file.blobUri,
  //     fileName: file.fileName || file.name,
  //     isError: file.isError || false,
  //   }));
  // }

  async openSubmitModal() {
    const temporaryResults = this.investigationResults().filter(
      (result: { visibilityStatus: string }) =>
        result.visibilityStatus === 'Temporary'
    );

    if (temporaryResults.length === 0) {
      alert('Δεν υπάρχουν προσωρινά αποθηκευμένα σχόλια.');
      return;
    }

    this.selectedComments = temporaryResults.flatMap(
      (result: { tags: any }) => result.tags
    );
    this.newComment = temporaryResults
      .map((result: { comment: any }) => result.comment)
      .join('\n');
    // this.toBeUploadedFiles = temporaryResults.flatMap(
    //   (result: {
    //     files: { blobUri: string; fileName: string; isError: boolean }[];
    //   }) => result.files
    // ); // Preload files

    //
  }


  async openSubmitTempDialog() { }
  async closeTempSubmit(): Promise<void> {
    if (this.selectedComments || this.newComment) {
      this.openSubmitTempDialog();
    } else {
      // if (
      //   this.submitTempModalConfig.shouldClose === undefined ||
      //   (await this.submitTempModalConfig.shouldClose())
      // ) {
      //   const result =
      //     this.submitTempModalConfig.onClose === undefined ||
      //     (await this.submitTempModalConfig.onClose());
      //   this.submitTempModal.close();
      // }
    }
  }
  async closeSubmitTempDialog(): Promise<void> {
    delete this.selectedComments;
    this.newComment = '';
    // this.submitTempPopupModal.close();
    // this.submitTempModal.close();
  }

  getToastMessage(isSuccess: boolean, message?: string) {
    if (message && isSuccess) {
      this.messageService.add({
        severity: 'success',
        summary: 'Success!',
        detail: message,
      });
      return;
    } else if (message && !isSuccess) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error!',
        detail: message,
      });
      return;
    }
    if (isSuccess) {
      this.messageService.add({
        severity: 'success',
        summary: 'Success!',
        detail: message,
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error!',
        detail: message,
      });
    }
  }
}
